import { TempoElement } from "@/utils/tempoElement";
import { v4 as uuidv4 } from "uuid";
import { shouldUseAiGenerationSafe } from "../../utils/aiGenerationUtils";
import type { DesignTokenApiResponse } from "@/components/DesignTokens/store/designTokensStore";
import type { NavTreeNode, Storyboard } from "@/utils/types";
import type { Page, PersistedPageConfig } from "./pagesStore";
import { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { generatePageFolderName } from "@/utils/fileNameUtils";

// Matches the file in tempo-devtools
export enum StylingFramework {
  INLINE = "Inline",
  CSS = "CSS",
  TAILWIND = "Tailwind",
}

export enum ChangeType {
  ADD_JSX = "ADD_JSX",
  MOVE_JSX = "MOVE_JSX",
  REMOVE_JSX = "REMOVE_JSX",
  ADD_AND_REMOVE_JSX = "ADD_AND_REMOVE_JSX",
  CHANGE_PROP = "CHANGE_PROP",
  UPDATE_CLASSES = "UPDATE_CLASSES",
  UPDATE_DESIGN_TOKENS = "UPDATE_DESIGN_TOKENS",
  EDIT_TEXT = "EDIT_TEXT",
  WRAP_DIV = "WRAP_DIV",
  CHANGE_TAG = "CHANGE_TAG",
  DUPLICATE = "DUPLICATE",
  UPDATE_STORYBOARD = "UPDATE_STORYBOARD",
  WRAP_STORYBOARDS = "WRAP_STORYBOARDS",
  UPDATE_PAGE = "UPDATE_PAGE",
  CREATE_PAGE = "CREATE_PAGE",
  DELETE_PAGE = "DELETE_PAGE",
  PERSIST_PAGE = "PERSIST_PAGE",
  UNPERSIST_PAGE = "UNPERSIST_PAGE",
  DUPLICATE_PAGE = "DUPLICATE_PAGE",
  UNDO = "UNDO",
  REDO = "REDO",
  DETACH_INSTANCE_JSX = "DETACH_INSTANCE_JSX",
  RENAME_FILE = "RENAME_FILE",
  REMOVE_FILE = "REMOVE_FILE",
  CREATE_STORY = "CREATE_STORY",
  ADD_LIBRARY_COMPONENT = "ADD_LIBRARY_COMPONENT",
  REMOVE_LIBRARY_COMPONENT = "REMOVE_LIBRARY_COMPONENT",
  UNKNOWN = "UNKNOWN",
}
export abstract class ChangeLedgerItem<T> {
  public id: string;
  public type: ChangeType;
  public changeName: string;
  public indexInLedger?: number;
  public changeFields: T;

  public asUndoOfActivityId?: string;
  public asRedoOfActivityId?: string;

  // These two fields are set after the API request completes
  public activityId?: string;

  public changeUndone?: boolean;
  public toastId?: string | number;
  public showedToastInsteadOfInstantUpdate?: boolean;
  public noApiRequest?: boolean;

  private _consumed: boolean;
  private _failed: boolean;

  private _instantUpdateSent: boolean;
  private _instantUpdateFinished: boolean;
  private _sendInstantUpdate: boolean;

  // Extra data given after the instant update (e.g. the inner HTML for deleted elements)
  public instantUpdateData?: any;
  public instantUpdateFailed?: boolean;
  public storyboardsAppliedTo: Set<string>;

  // Hot reload timestamp - used to determine if instant updates should be re-applied after hot reload
  public hotReloadTs?: number;

  // After instant update, which selected element key & multiselected element keys to set
  // Note -> we are not using callbacks for the undo instant update because we store this in
  // local storage then deserialize it, so the callbacks are lost
  // Also Note -> these element keys are selected explicitly in tempo-devtools
  // where the instant updates are performed (change item functions)

  // For instant update
  // Null will be set, while undefined will be ignored
  private elementKeyToSelectAfterInstantUpdate?: string | null;
  private elementKeysToMultiselectAfterInstantUpdate?: string[] | null;

  // For undo instant update
  // Null will be set, while undefined will be ignored
  private elementKeyToSelectAfterUndoInstantUpdate?: string | null;
  private elementKeysToMultiselectAfterUndoInstantUpdate?: string[] | null;

  // Resolvers after API call

  private _resolveApi?: (data?: any) => void;
  private _rejectApi?: (reason?: any) => void;
  private _responseValidator?: (data?: any) => string | null;
  private _apiRejectionAdded?: boolean;

  private _apiPromise: Promise<void>;

  constructor(
    type: ChangeType,
    changeName: string,
    changeFields: T,
    id?: string,
  ) {
    this.id = id || uuidv4();
    this.type = type;
    this.changeFields = changeFields;
    this.changeName = changeName;
    this._consumed = false;
    this._failed = false;
    this._instantUpdateSent = false;
    this._instantUpdateFinished = false;
    this._sendInstantUpdate = true;
    this.instantUpdateData = {};
    this.storyboardsAppliedTo = new Set();
    this.activityId = this.id;

    // Hot reload timestamp will be set when the change is executed

    this._apiPromise = new Promise<void>((resolve, reject) => {
      this._resolveApi = resolve;
      this._rejectApi = reject;
    });
  }

  public validateResponse(data?: any) {
    if (this._responseValidator) {
      return this._responseValidator(data);
    }
    return null;
  }

  public resolveApi(data?: any) {
    this._resolveApi?.(data);
  }

  public rejectApi(reason?: any) {
    if (this._apiRejectionAdded) {
      this._rejectApi?.(reason);
    }
  }

  public setResponseValidator(validator: (data?: any) => string | null) {
    this._responseValidator = validator;
  }

  public needsToSendInstantUpdate() {
    return !this._instantUpdateSent && this._sendInstantUpdate;
  }

  public markInstantUpdateSent() {
    this._instantUpdateSent = true;
  }

  public getInstantUpdateSent() {
    return this._instantUpdateSent;
  }

  public markProcessedSucceeded() {
    this._consumed = true;
  }

  public markProcessedFailed() {
    this._failed = true;
    this._consumed = true;
  }

  public isFailed() {
    return this._failed;
  }

  public needToProcessChange() {
    return !this._consumed;
  }

  public onApiResolve(onFulfilled: (data?: any) => void) {
    return this._apiPromise.then(onFulfilled);
  }

  public onApiReject(onRejected: (reason?: any) => void) {
    this._apiRejectionAdded = true;
    return this._apiPromise.catch(onRejected);
  }

  abstract prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ): {
    urlPath: string;
    body: {
      [key: string]: any;
    };
    successToastMessage?: string;
  } | null;

  public doNotSendInstantUpdate() {
    this._sendInstantUpdate = false;
  }

  // For selecting/deslecting new elements after instant updates

  public clearSelectedElementsAfterInstantUpdate() {
    this.elementKeyToSelectAfterInstantUpdate = null;
    this.elementKeysToMultiselectAfterInstantUpdate = null;
  }

  public setSelectedElementsAfterInstantUpdate(
    selectedElementKey: string,
    multiselectedElementKeys: string[] | null,
  ) {
    this.elementKeyToSelectAfterInstantUpdate = selectedElementKey;
    this.elementKeysToMultiselectAfterInstantUpdate = multiselectedElementKeys;
  }

  public clearSelectedElementsAfterUndoInstantUpdate() {
    this.elementKeyToSelectAfterUndoInstantUpdate = null;
    this.elementKeysToMultiselectAfterUndoInstantUpdate = null;
  }

  public setSelectedElementsAfterUndoInstantUpdate(
    selectedElementKey: string,
    multiselectedElementKeys: string[],
  ) {
    this.elementKeyToSelectAfterUndoInstantUpdate = selectedElementKey;
    this.elementKeysToMultiselectAfterUndoInstantUpdate =
      multiselectedElementKeys;
  }

  public getElementKeyToSelectAfterInstantUpdate() {
    return this.elementKeyToSelectAfterInstantUpdate;
  }

  public getElementKeysToMultiselectAfterInstantUpdate() {
    return this.elementKeysToMultiselectAfterInstantUpdate;
  }

  public getElementKeyToSelectAfterUndoInstantUpdate() {
    return this.elementKeyToSelectAfterUndoInstantUpdate;
  }

  public getElementKeysToMultiselectAfterUndoInstantUpdate() {
    return this.elementKeysToMultiselectAfterUndoInstantUpdate;
  }
}

/************************************************************************
 * Add Jsx Change
 ************************************************************************/

export interface AddJsxChangeFields {
  codebaseIdToAddTo?: string;
  beforeCodebaseId?: string;
  afterCodebaseId?: string;

  // The element to add
  addCodebaseId?: string;
  addNativeTag?: string;
  fileContentsToSourceFrom?: string;
  fileContentsSourceFilename?: string;

  propsToSet?: { [key: string]: any };

  // Used to track the storyboard in activity stream
  deletedStoryboardId?: string;

  // For instant update
  htmlForInstantUpdate?: string;
  navTreeNodesForInstantUpdate?: NavTreeNode[];

  // Extra files to create alongside the main component
  extraPathToContents?: { [path: string]: string };

  // For text only
  editTextAfterAdd?: boolean;

  // Required to transform the prev id to a new id in the codebase
  codebaseIdHashingKey: string;
}

export class AddJsxChange extends ChangeLedgerItem<AddJsxChangeFields> {
  constructor(changeFields: AddJsxChangeFields, id?: string) {
    super(ChangeType.ADD_JSX, "Add Element", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const {
      codebaseIdToAddTo,
      beforeCodebaseId,
      afterCodebaseId,
      addCodebaseId,
      addNativeTag,
      fileContentsToSourceFrom,
      fileContentsSourceFilename,
      propsToSet,
      deletedStoryboardId,
      htmlForInstantUpdate,
      extraPathToContents,
    } = this.changeFields;

    const body: any = {
      destinationElement: treeElementLookup[codebaseIdToAddTo || ""],
      beforeElement: treeElementLookup[beforeCodebaseId || ""],
      afterElement: treeElementLookup[afterCodebaseId || ""],
      newElement: {},
      canvasId,
      deletedStoryboardId,
      fileContentsToSourceFrom,
      fileContentsSourceFilename,
      extraFiles: extraPathToContents,
    };

    if (addCodebaseId) {
      body.newElement = {
        ...treeElementLookup[addCodebaseId],
      };
    } else if (addNativeTag) {
      body.newElement["type"] = "native";
      body.newElement["nativeTag"] = addNativeTag;
      body.newElement["componentName"] = addNativeTag;
    }

    if (propsToSet) {
      body.newElement["propsToSet"] = propsToSet;
    }

    if (!Object.keys(body.newElement).length) {
      delete body.newElement;
    }

    const isInstantUpdate = Boolean(htmlForInstantUpdate);
    body["isInstantUpdate"] = isInstantUpdate;

    // Determine whether to use AI generation based on the element being added
    let useAiGeneration = false;
    if (addCodebaseId) {
      const elementToAdd = treeElementLookup[addCodebaseId];
      useAiGeneration = shouldUseAiGenerationSafe(elementToAdd);
    }
    body["useAiGeneration"] = useAiGeneration;
    body["codebaseIdHashingKey"] = this.changeFields.codebaseIdHashingKey;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/addJsxElement`,
      body,

      // Only show the success message if we do not have instant updates
      successToastMessage: isInstantUpdate ? undefined : "Successfully added",
    };
  }
}

/************************************************************************
 * Remove JSX Change
 ************************************************************************/

export interface RemoveJsxChangeFields {
  codebaseIdsToRemove?: string[];
}

export class RemoveJsxChange extends ChangeLedgerItem<RemoveJsxChangeFields> {
  constructor(changeFields: RemoveJsxChangeFields, id?: string) {
    // Deduplicate the codebaseIdsToRemove
    changeFields.codebaseIdsToRemove = Array.from(
      new Set(changeFields.codebaseIdsToRemove),
    );
    super(ChangeType.REMOVE_JSX, "Delete Element", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { codebaseIdsToRemove } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/removeJsxElement`,
      body: {
        elementsToRemove: (codebaseIdsToRemove || [])
          .map((codebaseId: string) => treeElementLookup[codebaseId])
          .filter((element: any) => element),
      },
    };
  }
}

/************************************************************************
 * Add And Remove JSX Change
 ************************************************************************/

export interface AddAndRemoveJsxChangeFields {
  // Fields for the add operation
  addJsxChangeFields: AddJsxChangeFields;
  // Fields for the remove operation
  removeJsxChangeFields: RemoveJsxChangeFields;

  // Only for add and remove jsx change (not standalone)
  replaceCodebaseId?: string;
}

export class AddAndRemoveJsxChange extends ChangeLedgerItem<AddAndRemoveJsxChangeFields> {
  constructor(changeFields: AddAndRemoveJsxChangeFields, id?: string) {
    super(
      ChangeType.ADD_AND_REMOVE_JSX,
      changeFields.replaceCodebaseId ? "Detach Instance" : "Move Element",
      changeFields,
      id,
    );
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { addJsxChangeFields, removeJsxChangeFields } = this.changeFields;

    // Prepare add request body
    const addJsxChange = new AddJsxChange(addJsxChangeFields);
    const addApiRequest = addJsxChange.prepareApiRequest(
      canvasId,
      treeElementLookup,
    );

    // Prepare remove request body
    const removeJsxChange = new RemoveJsxChange(removeJsxChangeFields);
    const removeApiRequest = removeJsxChange.prepareApiRequest(
      canvasId,
      treeElementLookup,
    );

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/addAndRemoveJsxElement`,
      body: {
        addJsxBody: addApiRequest.body,
        removeJsxBody: removeApiRequest.body,
        replaceElement:
          treeElementLookup[this.changeFields.replaceCodebaseId || ""],
      },
    };
  }
}

/************************************************************************
 * Change Prop Change
 ************************************************************************/

export const DELETE_PROP_CONSTANT = "__TEMPO_DELETE_PROP_CONSTANT__"; // This needs to match the value in tempo-api
export interface ChangePropChangeFields {
  codebaseIdToChange: string;
  propName: string;
  propValue:
    | string
    | number
    | boolean
    | Record<string, any>
    | Array<any>
    | undefined
    | null
    | typeof DELETE_PROP_CONSTANT;
}

export class ChangePropChange extends ChangeLedgerItem<ChangePropChangeFields> {
  constructor(changeFields: ChangePropChangeFields, id?: string) {
    super(ChangeType.CHANGE_PROP, "Change Prop", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { codebaseIdToChange, propName, propValue } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/changePropValue`,
      body: {
        elementToModify: treeElementLookup[codebaseIdToChange],
        propName,
        propValue,
      },
      successToastMessage: "Prop changed",
    };
  }
}

/************************************************************************
 * Wrap in div change
 ************************************************************************/

export interface WrapDivChangeFields {
  codebaseIdsToWrap: string[];
  newCodebaseId: string;

  // For instant update undo
  originalNodeIndexesInParent?: {
    [codebaseId: string]: number;
  };
}

export class WrapDivChange extends ChangeLedgerItem<WrapDivChangeFields> {
  constructor(changeFields: WrapDivChangeFields, id?: string) {
    // Deduplicate the codebaseIdsToWrap
    changeFields.codebaseIdsToWrap = Array.from(
      new Set(changeFields.codebaseIdsToWrap),
    );
    super(ChangeType.WRAP_DIV, "Wrap In Div", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { codebaseIdsToWrap } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/wrapInDiv`,
      body: {
        reactElements: codebaseIdsToWrap.map(
          (codebaseId: string) => treeElementLookup[codebaseId],
        ),
        newCodebaseId: this.changeFields.newCodebaseId,
      },
    };
  }
}

/************************************************************************
 * Duplicate change
 ************************************************************************/

export interface DuplicateChangeFields {
  codebaseIdsToDuplicate: string[];
  codebaseIdHashingKey: string;
}

export class DuplicateChange extends ChangeLedgerItem<DuplicateChangeFields> {
  constructor(changeFields: DuplicateChangeFields, id?: string) {
    // Deduplicate the codebaseIdsToDuplicate
    changeFields.codebaseIdsToDuplicate = Array.from(
      new Set(changeFields.codebaseIdsToDuplicate),
    );
    super(ChangeType.DUPLICATE, "Duplicate", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { codebaseIdsToDuplicate } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/duplicate`,
      body: {
        reactElements: codebaseIdsToDuplicate.map(
          (codebaseId: string) => treeElementLookup[codebaseId],
        ),
        codebaseIdHashingKey: this.changeFields.codebaseIdHashingKey,
      },
    };
  }
}

/************************************************************************
 * Change Tag Change
 ************************************************************************/

export interface ChangeTagChangeFields {
  codebaseIdToChange: string;
  newTagName: string;
}

export class ChangeTagChange extends ChangeLedgerItem<ChangeTagChangeFields> {
  constructor(changeFields: ChangeTagChangeFields, id?: string) {
    super(ChangeType.CHANGE_TAG, "Change Tag Name", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { codebaseIdToChange, newTagName } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/changeElementTag`,
      body: {
        elementToModify: treeElementLookup[codebaseIdToChange],
        newTag: newTagName,
      },
    };
  }
}

/************************************************************************
 * Edit text change
 ************************************************************************/
export interface EditTextChangeFields {
  codebaseIdToEditText: string;
  newText: string;
  oldText?: string;
}

export class EditTextChange extends ChangeLedgerItem<EditTextChangeFields> {
  constructor(changeFields: EditTextChangeFields, id?: string) {
    super(ChangeType.EDIT_TEXT, "Edit Text", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { codebaseIdToEditText, newText, oldText } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/editText`,
      body: {
        element: treeElementLookup[codebaseIdToEditText],
        newText,
        oldText,
      },
    };
  }
}

export type AnyChangeLedgerItem = ChangeLedgerItem<any>;

/************************************************************************
 * Undo Change Type
 ************************************************************************/
export interface UndoChangeFields {
  changeToUndo: AnyChangeLedgerItem;
  matchingActivityFlushed?: boolean;
}

export class UndoChange extends ChangeLedgerItem<UndoChangeFields> {
  constructor(changeFields: UndoChangeFields, id?: string) {
    super(ChangeType.UNDO, "Undo", changeFields, id);
    this.noApiRequest = changeFields.changeToUndo.noApiRequest;
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/activities/undoChangeToFiles`,
      body: {},
    };
  }
}

/************************************************************************
 * Redo Change Type
 ************************************************************************/
export interface RedoChangeFields {
  changeToRedo: AnyChangeLedgerItem;
}

export class RedoChange extends ChangeLedgerItem<RedoChangeFields> {
  constructor(changeFields: RedoChangeFields, id?: string) {
    super(ChangeType.REDO, "Redo", changeFields, id);
    this.noApiRequest = changeFields.changeToRedo.noApiRequest;
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/activities/redoChangeToFiles`,
      body: {},
    };
  }
}

/************************************************************************
 * Update class change
 ************************************************************************/
export interface UpdateClassesChangeFields {
  codebaseIdToUpdateClass: string;

  newClasses: string[];
  oldClasses: string[]; // For undo

  // Involved classes (may needed to substitute for Tailwind JIT)
  involvedClasses: { tailwind: string; css: string; variant?: string | null }[];

  isInstantChangeOnly?: boolean;
  isForStoryboardSize?: boolean;
}

export class UpdateClassesChange extends ChangeLedgerItem<UpdateClassesChangeFields> {
  constructor(changeFields: UpdateClassesChangeFields, id?: string) {
    super(ChangeType.UPDATE_CLASSES, "Update Classes", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const {
      codebaseIdToUpdateClass,
      newClasses,
      oldClasses,
      isForStoryboardSize,
    } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/updateClasses`,
      body: {
        reactElement: treeElementLookup[codebaseIdToUpdateClass],
        newClasses,
        oldClasses,
        isForStoryboardSize,
      },
    };
  }
}

/************************************************************************
 * Update Design Tokens Change
 ************************************************************************/
export interface UpdateDesignTokensChangeFields {
  // For instant updates
  updatedTokenClasses: {
    tailwind: string;
    css: string;
    variant?: string | null;
  }[];

  // Tokens to delete (for delete operations)
  deletedTokenClasses?: {
    tailwind: string;
    variant?: string | null;
  }[];

  // Old values for undo support (for update operations)
  previousTokenClasses?: {
    tailwind: string;
    css: string;
    variant?: string | null;
  }[];

  // Stylesheet reference information for undo operations
  stylesheetInfo?: {
    [selector: string]: {
      stylesheetIndex: number; // Index in document.styleSheets
      ruleIndex: number; // Index of the rule within the stylesheet
    };
  };

  changedTokenPaths: string[]; // e.g. ["primary", "accent/default"]

  // For API persistence and undo/redo support
  operationType: "create" | "update" | "delete";
  apiPayload: DesignTokenApiResponse; // The payload to send to the server
  operationName: string; // Human-readable operation name
}

export class UpdateDesignTokensChange extends ChangeLedgerItem<UpdateDesignTokensChangeFields> {
  constructor(changeFields: UpdateDesignTokensChangeFields, id?: string) {
    super(
      ChangeType.UPDATE_DESIGN_TOKENS,
      changeFields.operationName || "Update Design Tokens",
      changeFields,
      id,
    );
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    // Use the stored API payload for server persistence
    return {
      urlPath: `canvases/${canvasId}/tailwind/colors`,
      body: this.changeFields.apiPayload,
      successToastMessage: `${this.changeFields.operationName} completed`,
    };
  }
}

/************************************************************************
 * Update storyboard change type - move, resize, create, delete, duplicate
 ************************************************************************/

export interface StoryboardDelta {
  changeType: "upsert" | "remove" | "update";
  storyboardId: string;
  pageId: string;
  pageConfigPath?: string; // Used to avoid searching for the file
  storyboardUpdateDict: Partial<Storyboard>;

  // Optional: For file rename operations when storyboard name changes
  oldFilePath?: string;
  newFilePath?: string;

  // Optional: For file creation (paste operations) - writes contents to newFilePath
  newFileContents?: string;
  // Optional: Source path for import rewriting (used with newFileContents)
  newFileContentsSourceFilename?: string;
}

export interface CreateStoryboardBody {
  componentName: string;
  addJsxBody?: {
    sourceElement:
      | JsxElement
      | {
          componentId?: string;
          nativeTag?: string;
          propsToSet?: any;
        }
      | {
          componentName: string;
          type: "native";
        };
    useAiGeneration: boolean;
    propsToSet?: { [prop: string]: any };
  };
  fileContents?: string;
  fileContentsSourceFilename?: string;
  extraFiles?: { [path: string]: string };
}

export interface UpdateStoryboardsChangeFields {
  canvasId: string;
  deltas: StoryboardDelta[];

  // E.g. for changing size or adding JSX - an API change that is associated with this storyboard change
  additionalGroupedChange?: AnyChangeLedgerItem;

  // If set, sets the values at the start of the action for undo
  valuesAtStartOfAction?: Record<string, Partial<Storyboard>>;

  // Create storyboard only
  createStoryboardFields?: {
    createStoryboardBody?: CreateStoryboardBody;
    editTextAfterCreate?: boolean;
    navTreeForInstantUpdate?: any;
    htmlForInstantUpdate?: string;
    removeJsxChangeFields?: RemoveJsxChangeFields;
  };

  // For delete only
  deleteStoryboardFields?: {
    storyboardIdsToDeleteOnBackend?: string[];
    storyTreeElementsToDeleteOnBackend?: JsxElement[];
    deleteSubcanvasId?: string;
  };

  // For duplicate only
  duplicateStoryboardFields?: {
    newStoryboardName: string;
    sourceStoryboardId: string;
  };

  codebaseIdHashingKey?: string;

  // To change the change type from just "Update Storyboards" (ex: Paste Storyboards)
  changeTypeOverride?: string;
}

export class UpdateStoryboardsChange extends ChangeLedgerItem<UpdateStoryboardsChangeFields> {
  constructor(changeFields: UpdateStoryboardsChangeFields, id?: string) {
    super(ChangeType.UPDATE_STORYBOARD, "Update Storyboard", changeFields, id);
    this.changeFields.codebaseIdHashingKey = uuidv4();

    const isCreate = Boolean(this.changeFields.createStoryboardFields);
    const isDelete = Boolean(this.changeFields.deleteStoryboardFields);
    const isDuplicate = Boolean(this.changeFields.duplicateStoryboardFields);

    // Validation for different types
    if ((isCreate || isDuplicate) && this.changeFields.deltas.length !== 1) {
      throw new Error(
        "Create storyboard body must be used with exactly one delta",
      );
    }

    if (
      this.changeFields.additionalGroupedChange &&
      (isCreate || isDelete || isDuplicate)
    ) {
      throw new Error(
        "Additional grouped change and create/delete storyboard body cannot be used together",
      );
    }

    const allowedAdditionalGroupedChanges = [
      ChangeType.ADD_JSX,
      ChangeType.UPDATE_CLASSES,
    ];
    if (
      this.changeFields.additionalGroupedChange?.type &&
      !allowedAdditionalGroupedChanges.includes(
        this.changeFields.additionalGroupedChange?.type,
      )
    ) {
      throw new Error(
        "Additional grouped change must be one of the allowed types",
      );
    }
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    if (this.changeFields.createStoryboardFields?.createStoryboardBody) {
      const body: any = {
        ...this.changeFields.createStoryboardFields.createStoryboardBody,
        storyboardId: this.changeFields.deltas[0].storyboardId,
        pageConfigPath: this.changeFields.deltas[0].pageConfigPath,
        pageId: this.changeFields.deltas[0].pageId,
        storyboardData: this.changeFields.deltas[0].storyboardUpdateDict,
        canvasId: canvasId,
        codebaseIdHashingKey: this.changeFields.codebaseIdHashingKey,
      };

      // Convert removeJsxChangeFields codebase IDs to JSX elements (same pattern as RemoveJsxChange.prepareApiRequest)
      if (this.changeFields.createStoryboardFields.removeJsxChangeFields) {
        const { codebaseIdsToRemove } =
          this.changeFields.createStoryboardFields.removeJsxChangeFields;
        const elementsToRemove = (codebaseIdsToRemove || [])
          .map((codebaseId: string) => treeElementLookup[codebaseId])
          .filter(Boolean);

        if (elementsToRemove.length > 0) {
          body.removeJsxChangeFields = {
            elementsToRemove,
          };
        }
      }

      return {
        urlPath: `canvases/${canvasId}/parseAndMutate/mutate/createUnsavedComponent`,
        body,
      };
    }

    if (
      this.changeFields.deleteStoryboardFields?.storyboardIdsToDeleteOnBackend
        ?.length ||
      this.changeFields.deleteStoryboardFields
        ?.storyTreeElementsToDeleteOnBackend?.length
    ) {
      const body: any = {
        storyboardIds:
          this.changeFields.deleteStoryboardFields
            .storyboardIdsToDeleteOnBackend,
        storyTreeElements:
          this.changeFields.deleteStoryboardFields
            .storyTreeElementsToDeleteOnBackend,
        subCanvasId: this.changeFields.deleteStoryboardFields.deleteSubcanvasId,
        codebaseIdHashingKey: this.changeFields.codebaseIdHashingKey,
        storyboardDeltas: this.changeFields.deltas, // Include deltas to update page files
      };

      return {
        urlPath: `canvases/${canvasId}/parseAndMutate/mutate/deleteUnsavedComponent`,
        body,
      };
    }

    if (this.changeFields.duplicateStoryboardFields) {
      const body: any = {
        componentName:
          this.changeFields.duplicateStoryboardFields.newStoryboardName,
        sourceStoryboardId:
          this.changeFields.duplicateStoryboardFields.sourceStoryboardId,
        newStoryboardId: this.changeFields.deltas[0].storyboardId,
        codebaseIdHashingKey: this.changeFields.codebaseIdHashingKey,
        // Include storyboardDeltas to update page.json (pageConfigPath is in each delta)
        storyboardDeltas: this.changeFields.deltas,
      };

      return {
        urlPath: `canvases/${canvasId}/parseAndMutate/mutate/duplicateUnsavedComponent`,
        body,
      };
    }

    // For operations that update page.json (move/resize operations), use dedicated endpoint
    // Note: This is the way it is because the activity system doesn't really
    // work well with having multiple endpoints for a single activity.  This
    // should be reworked to just handle arbitrary additionalGrouupedChanges
    // using the same logic they'd normally use
    if (this.changeFields.deltas.length > 0) {
      const body: any = {
        storyboardDeltas: this.changeFields.deltas,
      };

      // Pass changeType override for activity tracking (e.g., PASTE_STORYBOARDS)
      if (this.changeFields.changeTypeOverride) {
        body.changeType = this.changeFields.changeTypeOverride;
      }

      // If there's an additionalGroupedChange that's an UpdateClassesChange, serialize it into stylingChanges
      if (
        this.changeFields.additionalGroupedChange?.type ===
          ChangeType.UPDATE_CLASSES &&
        !this.changeFields.additionalGroupedChange.noApiRequest
      ) {
        const stylingChange = this.changeFields
          .additionalGroupedChange as UpdateClassesChange;
        body.stylingChanges = [
          {
            reactElement:
              treeElementLookup[
                stylingChange.changeFields.codebaseIdToUpdateClass
              ],
            newClasses: stylingChange.changeFields.newClasses,
            oldClasses: stylingChange.changeFields.oldClasses,
          },
        ];
      }

      // If there's an additionalGroupedChange that's an AddJsxChange, serialize it into addJsxChanges
      if (
        this.changeFields.additionalGroupedChange?.type ===
          ChangeType.ADD_JSX &&
        !this.changeFields.additionalGroupedChange.noApiRequest
      ) {
        const addJsxChange = this.changeFields
          .additionalGroupedChange as AddJsxChange;
        const fields = addJsxChange.changeFields;

        // Build newElement based on what type of element is being added
        let newElement: any = undefined;
        if (fields.addCodebaseId) {
          newElement = { ...treeElementLookup[fields.addCodebaseId] };
          if (fields.propsToSet) {
            newElement.propsToSet = fields.propsToSet;
          }
        } else if (fields.addNativeTag) {
          newElement = {
            type: "native",
            nativeTag: fields.addNativeTag,
            componentName: fields.addNativeTag,
          };
          if (fields.propsToSet) {
            newElement.propsToSet = fields.propsToSet;
          }
        }

        body.addJsxChanges = [
          {
            destinationElement:
              treeElementLookup[fields.codebaseIdToAddTo || ""],
            beforeElement: treeElementLookup[fields.beforeCodebaseId || ""],
            afterElement: treeElementLookup[fields.afterCodebaseId || ""],
            newElement,
            fileContentsToSourceFrom: fields.fileContentsToSourceFrom,
            fileContentsSourceFilename: fields.fileContentsSourceFilename,
            extraFiles: fields.extraPathToContents,
          },
        ];
        body.codebaseIdHashingKey = this.changeFields.codebaseIdHashingKey;
      }

      return {
        urlPath: `canvases/${canvasId}/pages/update-storyboards`,
        body,
      };
    }

    // For additionalGroupedChange without deltas (non-UPDATE_CLASSES cases like ADD_JSX)
    const additionalGroupedChangeWithApiRequest =
      this.changeFields.additionalGroupedChange &&
      !this.changeFields.additionalGroupedChange.noApiRequest;
    if (additionalGroupedChangeWithApiRequest) {
      return (
        this.changeFields.additionalGroupedChange?.prepareApiRequest(
          canvasId,
          treeElementLookup,
        ) ?? null
      );
    }

    return null;
  }
}

/************************************************************************
 * Wrap Storyboards Change
 *
 * Wraps multiple storyboards into a single new storyboard with a div wrapper
 ************************************************************************/
export interface WrapStoryboardsChangeFields {
  canvasId: string;
  wrapDivClasses: string;
  deltas: StoryboardDelta[];
  storyboardName: string;
  newStoryboardId: string;

  // Files to be wrapped
  sourceFiles: { fileContents: string; filename: string }[];

  navTreeForInstantUpdate?: any;
  htmlForInstantUpdate?: string;

  codebaseIdHashingKey?: string;

  // Page info for updating page.json
  pageConfigPath: string;

  // Frontend-computed destination file path for new readable filename format
  destinationFilePath?: string;
}

export class WrapStoryboardsChange extends ChangeLedgerItem<WrapStoryboardsChangeFields> {
  constructor(changeFields: WrapStoryboardsChangeFields, id?: string) {
    changeFields.codebaseIdHashingKey = uuidv4();
    super(ChangeType.WRAP_STORYBOARDS, "Wrap Storyboards", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { sourceFiles, wrapDivClasses, destinationFilePath } =
      this.changeFields;

    const body: any = {
      sourceFiles,
      wrapDivClasses,
      componentName: this.changeFields.storyboardName,
      storyboardId: this.changeFields.newStoryboardId,
      codebaseIdHashingKey: this.changeFields.codebaseIdHashingKey,
      pageConfigPath: this.changeFields.pageConfigPath,
      destinationFilePath,
    };

    // Add page info for updating page.json
    if (this.changeFields.pageConfigPath) {
      body.pageConfigPath = this.changeFields.pageConfigPath;
      body.storyboardDeltas = this.changeFields.deltas;
    }

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/createUnsavedComponentFromWrap`,
      body,
    };
  }
}

/************************************************************************
 * Create Story Change
 *
 * Creates a new story variant in a component's story file from an instance.
 ************************************************************************/
export interface CreateStoryChangeFields {
  storyFilePath: string | null; // Existing story file or null for new
  fileContents: string; // DragData.fileContents
  fileContentsSourceFilename: string;
  storyName: string;
  componentName: string;
  componentFilepath: string;
  isDefaultExport?: boolean; // Whether the component is a default export
}

export class CreateStoryChange extends ChangeLedgerItem<CreateStoryChangeFields> {
  constructor(changeFields: CreateStoryChangeFields, id?: string) {
    super(ChangeType.CREATE_STORY, "Create Story", changeFields, id);
    // No instant update for this change type
    this.doNotSendInstantUpdate();
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const {
      storyFilePath,
      fileContents,
      fileContentsSourceFilename,
      storyName,
      componentName,
      componentFilepath,
      isDefaultExport,
    } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/createStory`,
      body: {
        storyFilePath,
        fileContents,
        fileContentsSourceFilename,
        storyName,
        componentName,
        componentFilepath,
        isDefaultExport,
      },
      successToastMessage: `Saved as story: ${storyName}`,
    };
  }
}

/************************************************************************
 * Unknown Change Type
 *
 * This change type is created from the activity stream when the change
 * ledger goes out of sync with it. This allows the user to still undo
 * that change.
 *
 * This can happen if for example a canvas is being shared and another
 * user makes a change in between this users' changes
 ************************************************************************/
export interface UnknownChangeFields {}

export class UnknownChange extends ChangeLedgerItem<UnknownChangeFields> {
  constructor(changeFields: UnknownChangeFields, id?: string) {
    super(ChangeType.UNKNOWN, "", changeFields, id);
    // Do not process unknown changes
    this.markProcessedSucceeded();
    this.doNotSendInstantUpdate();
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    throw Error("Unsupported operation");

    // For typing
    return {
      urlPath: ``,
      body: {},
    };
  }
}

/************************************************************************
 * Update Page Change
 *
 * Used for renaming pages and other page property updates
 ************************************************************************/
export interface UpdatePageChangeFields {
  canvasId: string;
  pageId: string;
  pageConfigPath: string;
  updates: {
    name?: string;
    // Future: other page properties can be added here
  };
  previousValues: {
    name?: string;
    // Future: other page properties
  };

  // Optional: For folder rename operations when page name changes
  oldFolderPath?: string;
  newFolderPath?: string;
}

export class UpdatePageChange extends ChangeLedgerItem<UpdatePageChangeFields> {
  constructor(changeFields: UpdatePageChangeFields, id?: string) {
    // Determine change name based on what's being updated
    const changeName =
      changeFields.updates.name !== undefined ? "Rename Page" : "Update Page";
    super(ChangeType.UPDATE_PAGE, changeName, changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/pages/update`,
      body: {
        pageId: this.changeFields.pageId,
        pageConfigPath: this.changeFields.pageConfigPath,
        updates: this.changeFields.updates,
        activityId: this.activityId,
        // Include folder rename paths if provided
        oldFolderPath: this.changeFields.oldFolderPath,
        newFolderPath: this.changeFields.newFolderPath,
      },
      successToastMessage: undefined, // No toast on success (instant update already shows feedback)
    };
  }
}

/************************************************************************
 * Create Page Change
 ************************************************************************/
export interface CreatePageChangeFields {
  canvasId: string;
  page?: Partial<PersistedPageConfig>;
  configPath?: string;

  // For undo: store the created page data
  createdPageConfigPath?: string;
}

export class CreatePageChange extends ChangeLedgerItem<CreatePageChangeFields> {
  constructor(changeFields: CreatePageChangeFields, id?: string) {
    super(ChangeType.CREATE_PAGE, "Create Page", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/pages/create`,
      body: {
        page: this.changeFields.page,
        configPath: this.changeFields.configPath,
        activityId: this.activityId,
      },
      successToastMessage: undefined, // Instant update shows feedback
    };
  }
}

/************************************************************************
 * Delete Page Change
 ************************************************************************/
export interface DeletePageChangeFields {
  canvasId: string;
  pageId: string;
  pageConfigPath: string;

  // For undo: store the full page data
  deletedPageData?: Page;
}

export class DeletePageChange extends ChangeLedgerItem<DeletePageChangeFields> {
  constructor(changeFields: DeletePageChangeFields, id?: string) {
    super(ChangeType.DELETE_PAGE, "Delete Page", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/pages/delete`,
      body: {
        pageId: this.changeFields.pageId,
        pageConfigPath: this.changeFields.pageConfigPath,
        activityId: this.activityId,
      },
      successToastMessage: undefined, // Instant update shows feedback
    };
  }
}

/************************************************************************
 * Duplicate Page Change
 ************************************************************************/
export interface DuplicatePageChangeFields {
  canvasId: string;
  sourcePageConfigPath: string;
  newPageId: string;
  newPageName: string;
}

export class DuplicatePageChange extends ChangeLedgerItem<DuplicatePageChangeFields> {
  constructor(changeFields: DuplicatePageChangeFields, id?: string) {
    super(ChangeType.DUPLICATE_PAGE, "Duplicate Page", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    const { sourcePageConfigPath, newPageId, newPageName } = this.changeFields;

    // Check if this is a managed folder-based page: (tempobook|tempo)/pages/[folder]/[file].page.json
    const managedFolderMatch = sourcePageConfigPath.match(
      /^(.+\/(tempobook|tempo)\/pages)\/[^/]+\/([^/]+\.page\.json)$/,
    );

    let destPageConfigPath: string;
    if (managedFolderMatch) {
      const pagesDir = managedFolderMatch[1];
      const configFileName = managedFolderMatch[3];
      const newFolderName = generatePageFolderName(newPageName, newPageId);
      destPageConfigPath = `${pagesDir}/${newFolderName}/${configFileName}`;
    } else {
      const directory = sourcePageConfigPath.replace(/\/[^/]+$/, "");
      const newFilename = `${generatePageFolderName(newPageName, newPageId)}.page.json`;
      destPageConfigPath = `${directory}/${newFilename}`;
    }

    return {
      urlPath: `canvases/${canvasId}/pages/copy`,
      body: {
        sourcePageConfigPath,
        destPageConfigPath,
        newPageId,
        newPageName,
        activityId: this.activityId,
      },
      successToastMessage: undefined,
    };
  }
}

export interface MovePageChangeFields {
  canvasId: string;
  pageId: string;
  pageConfigPath: string;
  sourcePath: string;
  destPath: string;
  activityType: "PERSIST_PAGE" | "UNPERSIST_PAGE";
}

export class MovePageChange extends ChangeLedgerItem<MovePageChangeFields> {
  constructor(changeFields: MovePageChangeFields, id?: string) {
    const changeName =
      changeFields.activityType === "PERSIST_PAGE"
        ? "Save Page to Codebase"
        : "Remove Page from Codebase";
    const changeType =
      changeFields.activityType === "PERSIST_PAGE"
        ? ChangeType.PERSIST_PAGE
        : ChangeType.UNPERSIST_PAGE;
    super(changeType, changeName, changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/pages/move`,
      body: {
        pageId: this.changeFields.pageId,
        pageConfigPath: this.changeFields.pageConfigPath,
        sourcePath: this.changeFields.sourcePath,
        destPath: this.changeFields.destPath,
        activityId: this.activityId,
        activityType: this.changeFields.activityType,
      },
      successToastMessage: undefined, // Instant update shows feedback
    };
  }
}

/************************************************************************
 * Add Library Component Change
 ************************************************************************/
export interface AddLibraryComponentChangeFields {
  canvasId: string;
  componentPath: string; // Canvas-relative path without extension
  componentName?: string; // Export name (e.g., "Button")
  libraryId: string; // Library UUID - frontend always provides this (existing or new)
}

export class AddLibraryComponentChange extends ChangeLedgerItem<AddLibraryComponentChangeFields> {
  constructor(changeFields: AddLibraryComponentChangeFields, id?: string) {
    super(ChangeType.ADD_LIBRARY_COMPONENT, "Add to Library", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/config/addLibraryComponent`,
      body: {
        componentPath: this.changeFields.componentPath,
        componentName: this.changeFields.componentName,
        libraryId: this.changeFields.libraryId,
        activityId: this.activityId,
      },
      successToastMessage: undefined,
    };
  }
}

/************************************************************************
 * Remove Library Component Change
 ************************************************************************/
export interface RemoveLibraryComponentChangeFields {
  canvasId: string;
  componentPath: string; // Canvas-relative path without extension
  componentName?: string; // Export name (e.g., "Button")
  libraryId: string; // Library UUID - passed from AssetComponent.libraryId
}

export class RemoveLibraryComponentChange extends ChangeLedgerItem<RemoveLibraryComponentChangeFields> {
  constructor(changeFields: RemoveLibraryComponentChangeFields, id?: string) {
    super(
      ChangeType.REMOVE_LIBRARY_COMPONENT,
      "Remove from Library",
      changeFields,
      id,
    );
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any },
  ) {
    return {
      urlPath: `canvases/${canvasId}/config/removeLibraryComponent`,
      body: {
        componentPath: this.changeFields.componentPath,
        componentName: this.changeFields.componentName,
        libraryId: this.changeFields.libraryId,
        activityId: this.activityId,
      },
      successToastMessage: undefined,
    };
  }
}
